// Lead Drawer's "Sample/Trial" tab — thin wrapper around the REAL Sampling
// module (features/sampling/*, backend /api/sampling), not a parallel
// system. Reuses its existing components exactly as SamplingPage.tsx does:
// SampleFormModal already accepts a `defaultLeadId` prop built for this
// purpose, and SampleDetailDrawer is the full self-contained detail/
// workflow view (dispatch, feedback, technical approval, convert to
// quotation). Nothing here re-implements that workflow.
//
// Supersedes an earlier SampleTrialPanel.tsx that called /api/sample-trials
// — a route that was never mounted in server.js, so that panel never
// actually worked. That backend (src/db-sample-trials.js) predates the
// complete Sampling module (git: "checkpoint: SLA activation, Sample/Trial
// sub-flow..." vs. the later "feat(rbm-core): complete connected enterprise
// workflow, sampling, ...") and was superseded by it, just never wired to
// this drawer or cleaned up.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FlaskConical } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Spinner } from '@/shared/components/ui/Spinner';
import { ROUTES } from '@/routes/paths';
import { useSampleRequests } from '@/features/sampling/api';
import { SampleDetailDrawer } from '@/features/sampling/components/SampleDetailDrawer';
import type { SampleState } from '@/features/sampling/types';

const STATUS_TONE: Record<SampleState, BadgeTone> = {
  draft: 'neutral',
  to_dispatch: 'warning',
  dispatched: 'info',
  delivered: 'info',
  feedback_received: 'warning',
  technical_review: 'warning',
  converted: 'success',
  closed_lost: 'danger',
  cancelled: 'neutral',
};

const STATUS_LABEL: Record<SampleState, string> = {
  draft: 'Draft',
  to_dispatch: 'To Dispatch',
  dispatched: 'In Transit',
  delivered: 'Delivered',
  feedback_received: 'Feedback Received',
  technical_review: 'Tech Review',
  converted: 'Converted',
  closed_lost: 'Closed (Lost)',
  cancelled: 'Cancelled',
};

export function LeadSamplingPanel({ leadId, canMutate }: { leadId: string; canMutate: boolean }) {
  const navigate = useNavigate();
  const { data: listData, isLoading } = useSampleRequests({ leadId });
  const samples = listData?.items || [];
  const [detailId, setDetailId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex h-24 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {canMutate && (
        <div className="flex justify-end">
          <Button size="sm" onClick={() => navigate(`${ROUTES.samplingNew}?recordId=${encodeURIComponent(leadId)}`)}>
            <Plus className="mr-1 h-4 w-4" /> Request Sample
          </Button>
        </div>
      )}

      {samples.length === 0 ? (
        <EmptyState
          icon={<FlaskConical className="h-6 w-6" />}
          title="No sample requested yet"
          description="Once a sample is needed for this lead, request it above."
        />
      ) : (
        <div className="space-y-2">
          {samples.map((s) => (
            <div
              key={s._id}
              className="cursor-pointer rounded-card-sm border border-outline bg-surface p-3 transition-colors hover:border-primary/40"
              onClick={() => setDetailId(s._id)}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-data-md font-semibold text-on-surface">{s.code}</span>
                <Badge tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</Badge>
              </div>
              <div className="mt-1 truncate text-body-sm text-on-surface-variant">
                {s.lines?.map((l) => l.productName || l.sku).filter(Boolean).join(', ') || 'No products listed'}
              </div>
            </div>
          ))}
        </div>
      )}

      <SampleDetailDrawer sampleId={detailId} open={!!detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
